import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AGENT_INTERCEPTION_CONFIRM_CONTENT } from '../../constants';
import { AIStatusColors } from '../../redux/features/ai/ai.constants';
import {
  selectAIStatus,
  selectIsAIAutoMode,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectIsAIEscalatedOnCarEnter,
  selectRequiresTakeover,
} from '../../redux/features/ai/ai.selector';
import { selectFeatureFlagUnresolvedItems } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import getStatusBarColor from '../../utils/getStatusBarColor';
import ConfirmDialog from '../common/ConfirmDialog';

interface IAIStatusBarStyleProps {
  isAIAutoMode: boolean;
  isAIEscalation: boolean;
  isAIEscalatedOnCarEnter: boolean;
  aiStatusColor: AIStatusColors;
}

interface IAgentInterceptionConfirmDialog {
  open: boolean;
  confirm: boolean;
}

const useStyles = makeStyles<Theme, IAIStatusBarStyleProps>(({ spacing }) => ({
  // Background "throb" between bright and dark red for a true takeover —
  // clearly distinguishable from a static red bar, but a slow 1s ease-in-out
  // cycle (1Hz), well under the WCAG 2.3.1 three-flash threshold, and it stays
  // red (no high-luminance strobe).
  '@keyframes takeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' }, // light red
    '50%': { backgroundColor: '#990000' }, // dark red
  },
  aiStatusBarClass: {
    width: '100%',
    height: '48px',
    padding: spacing(1.5),
    color: ({ isAIEscalation }) => (isAIEscalation ? Colors.white : 'inherit'),
    overflowY: 'auto',
  },
  // Applied only on a true takeover. Kept as a static rule (not a dynamic
  // function value) so JSS resolves the "$takeoverPulse" keyframes reference
  // to its scoped name — the reference does not resolve inside fn values.
  // NOTE: intentionally does NOT honor prefers-reduced-motion — a true takeover
  // is a critical operational alert the agent must not miss, so the throb stays
  // on even when the OS reduce-motion setting is enabled.
  takeoverPulseClass: {
    animation: '$takeoverPulse 1s ease-in-out infinite',
  },
  emptyBarClass: {
    fontSize: '1rem',
    display: 'flex',
    height: '100%',
    flexDirection: 'row',
    columnGap: '8px',
    alignItems: 'flex-start',
    lineHeight: '30px',
  },
  boldFontClass: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  aiMsgClass: {
    wordBreak: 'break-all',
  },
}));

const AIStatusBar: FC = (props) => {
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isAIEscalation = useSelector(selectIsAIEscalation);
  const isAIEscalatedOnCarEnter = useSelector(selectIsAIEscalatedOnCarEnter);
  const requiresTakeover = useSelector(selectRequiresTakeover);
  const unresolvedItemsEnabled = useSelector(selectFeatureFlagUnresolvedItems);
  const aiStatus = useSelector(selectAIStatus);
  const aiEscalationReason = useSelector(selectAIEscalationReason);

  // Every escalation is red. A true takeover is distinguished only by the
  // pulse animation, and only when the unresolved-items feature is on (off =
  // legacy: red, no pulse, no dependency on the backend TAKEOVER marker).
  const showPulse = unresolvedItemsEnabled && requiresTakeover;

  const {
    aiStatusBarClass,
    takeoverPulseClass,
    emptyBarClass,
    boldFontClass,
    aiMsgClass,
  } = useStyles({
    isAIAutoMode,
    isAIEscalation,
    isAIEscalatedOnCarEnter,
    aiStatusColor: aiStatus.status,
  });
  const confirmAgentInterception =
    isAIAutoMode && aiStatus.status === AIStatusColors.green && !isAIEscalation;
  const [agentInterceptionConfirmDialog, setAgentInterceptionConfirmDialog] =
    useState<IAgentInterceptionConfirmDialog>({ open: false, confirm: false });

  useEffect(() => {
    if (confirmAgentInterception && !agentInterceptionConfirmDialog.confirm) {
      const handleKeyboardMouseEvent = (event: Event) => {
        // Skip interactions that aren't "taking over the order": the header, the
        // account menu, the Layout-preference dialog, and the pane resize
        // handles (some render in portals, outside the header DOM).
        const target = event.target as HTMLElement;
        const ignoredSelectors = [
          '[data-header="true"]',
          '[data-profile-menu="true"]',
          '[data-layout-preference="true"]',
          '[data-resize-handle]',
        ];
        if (
          target &&
          typeof target.closest === 'function' &&
          ignoredSelectors.some((selector) => target.closest(selector))
        ) {
          return;
        }
        setAgentInterceptionConfirmDialog((prevState) => ({
          ...prevState,
          open: true,
        }));
      };
      document.addEventListener('keydown', handleKeyboardMouseEvent, true);
      document.addEventListener('mousedown', handleKeyboardMouseEvent, true);
      return () => {
        document.removeEventListener('keydown', handleKeyboardMouseEvent, true);
        document.removeEventListener(
          'mousedown',
          handleKeyboardMouseEvent,
          true
        );
      };
    }
  }, [confirmAgentInterception, agentInterceptionConfirmDialog]);

  const aiStatusBarMessage = isAIEscalation
    ? aiEscalationReason || aiStatus.message
    : aiStatus.message;

  const aiStatusBarBGColor = getStatusBarColor(
    isAIEscalation,
    isAIEscalatedOnCarEnter,
    aiStatus.status
  );

  return (
    <div
      data-testid="ai-status-bar"
      data-requires-takeover={requiresTakeover}
      className={`${aiStatusBarClass}${
        showPulse ? ` ${takeoverPulseClass}` : ''
      }`}
      style={{
        backgroundColor: aiStatusBarBGColor,
      }}
    >
      <div className={emptyBarClass}>
        <div className={boldFontClass}>AUTO AI: </div>
        <div className={aiMsgClass} title={aiStatusBarMessage}>
          {aiStatusBarMessage}
        </div>
      </div>
      <ConfirmDialog
        open={agentInterceptionConfirmDialog.open}
        content={AGENT_INTERCEPTION_CONFIRM_CONTENT}
        onClose={() =>
          setAgentInterceptionConfirmDialog((prevState) => ({
            ...prevState,
            open: false,
          }))
        }
        onConfirm={() =>
          setAgentInterceptionConfirmDialog({ open: false, confirm: true })
        }
        closeBtnText={'No'}
        confirmBtnText={'Yes'}
      />
    </div>
  );
};

export default AIStatusBar;
